import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import type { HealthStatus } from '@kick/types';
import { rootApi } from '@/lib/api';
import { cn } from '@/lib/cn';

/**
 * Indicador del estado del backend.
 * Durante el desarrollo permite ver de un vistazo si PostgreSQL, Redis
 * o MinIO están caídos, sin abrir la consola.
 */
export function HealthBadge() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['health'],
    queryFn: async () => (await rootApi.get<HealthStatus>('/health')).data,
    refetchInterval: 30_000,
    retry: 1,
  });

  if (isLoading) {
    return (
      <Shell className="border-kick-gray/30 text-kick-gray">
        <Loader2 className="h-4 w-4 animate-spin" />
        Conectando…
      </Shell>
    );
  }

  if (isError || !data) {
    return (
      <Shell className="border-red-300 bg-red-50 text-red-700">
        <XCircle className="h-4 w-4" />
        API sin conexión
      </Shell>
    );
  }

  const styles = {
    ok: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    degraded: 'border-amber-300 bg-amber-50 text-amber-700',
    down: 'border-red-300 bg-red-50 text-red-700',
  } as const;

  const Icon = data.status === 'ok' ? CheckCircle2 : data.status === 'degraded' ? AlertTriangle : XCircle;

  return (
    <Shell className={styles[data.status]}>
      <Icon className="h-4 w-4" />
      <span className="font-semibold">
        {data.status === 'ok' ? 'Sistema operativo' : data.status === 'degraded' ? 'Degradado' : 'Caído'}
      </span>
      <span className="hidden gap-2 sm:flex">
        <Dot label="BD" ok={data.services.db.status === 'ok'} />
        <Dot label="Redis" ok={data.services.redis.status === 'ok'} />
        <Dot label="Archivos" ok={data.services.storage.status === 'ok'} />
      </span>
    </Shell>
  );
}

function Shell({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm',
        className,
      )}
    >
      {children}
    </div>
  );
}

function Dot({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs">
      <span className={cn('h-2 w-2 rounded-full', ok ? 'bg-emerald-500' : 'bg-red-500')} />
      {label}
    </span>
  );
}
